import React, { ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { maskAccountNumber } from '@ally/aos-utils'
import {
  Account,
  AccountLOB,
  AccountStatus,
  AccountType,
} from '@ally/data-types-afg-accounts'
import { ScreenReaderOnly, TextBody } from '@ally/metronome-ui'
import { trackCCAccount } from '../../analytics'
import { getAccountName, isRestrictedCCStatus } from '../../utils'

const StyledWrapper = styled.div`
  ${({ theme: { media } }): string =>
    `
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    ${media.up('xl')} {
      display: inline;
    }
    `};
`

// TODO: Check if MuiLink can be used
const StyledAnchor = styled(Link)(({ theme }) => theme.buttons.link)

const SharedAccountStyles = css`
  text-transform: capitalize;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
  vertical-align: middle;
  display: inline-block;

  @media print {
    display: inline;
  }
`

const StyledLink = styled(StyledAnchor)`
  ${({ theme: { colors } }): string => `
    ${SharedAccountStyles}

    @media print {
      color: ${colors['slate-5']};
    }
  `};
`

const StyledAccountNameText = styled(TextBody)`
  ${SharedAccountStyles}
`

const StyledButton = styled.button(({ theme }) => theme.buttons.link)

export const StyledMortgageButton = styled(StyledButton)`
  ${({ theme: { colors, media } }): string =>
    `
    max-width: 100%;

    > span {
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 100%;
      vertical-align: middle;
      display: inline-block;

      ${media.down('md')} {
        width: 100%;
      }

      @media print {
        display: inline;
        color: ${colors['slate-5']};
      }
    }
  `};
`

export const StyledRestrictedAccount = styled(TextBody)`
  ${SharedAccountStyles}
`

export const AccountDetailsLink: React.FC<{
  className?: string
  text: string | ReactNode
  key?: string
  allytm?: string
  account: Account
  hasStaleData?: boolean
  showAsText?: boolean
}> = ({
  account,
  className,
  text,
  key,
  allytm,
  hasStaleData,
  showAsText,
}): ReactElement => {
  const { type, id, nickName, name, accountNumber } = account
  const acctDetailsRoute = `/account/${id}/details`

  function accountDetailsUrl(accountType: AccountType | undefined): string {
    if (hasStaleData) {
      return `/error`
    }
    switch (accountType) {
      // istanbul ignore next
      case 'INVS':
        return `/sso/invest/accounts/holdings-balances/${id}`
      // istanbul ignore next
      case 'INVW':
        return `/sso/wealth/account-details/${id}`
      // istanbul ignore next
      case 'INVA':
        return `/sso/invest/dashboard/${id}`
      case 'LOAN':
      case 'OPERATING_LEASE':
      case 'FINANCE_LEASE':
      case 'HIRE_PURCHASE':
        return `/sso/auto/accounts/${id}`
      case 'PLATINUM':
      case 'EVERYDAY':
      case 'UNLIMITED':
        return `/sso/credit-card/Account/Overview?accountProxyId=${id}`
      case 'MORTGAGE':
      case 'HOME_EQUITY':
        return `/sso/home-loans/dashboard/?id=${accountNumber}`
      // istanbul ignore next
      default:
        return acctDetailsRoute
    }
  }

  // Wealth Overview
  if (account.lob === AccountLOB.WEALTH && account.id === 'wealth-overview') {
    return (
      <StyledWrapper>
        <StyledLink
          className={className}
          key={key}
          data-allytmln={allytm}
          data-testid="wealth-overview-link"
          to="/sso/wealth/wealth-overview"
        >
          {text}
        </StyledLink>
      </StyledWrapper>
    )
  }

  const sharedProps = {
    className,
    children: text,
    key,
    'data-allytmln': allytm,
    'aria-label': `${getAccountName(
      nickName,
      name,
    )} account ending in ${maskAccountNumber(accountNumber, 'none')}`,
  }

  if (showAsText) {
    return (
      <StyledWrapper>
        <StyledAccountNameText tag="span" size="sm" {...sharedProps}>
          {text}
        </StyledAccountNameText>
      </StyledWrapper>
    )
  }

  const hasCurrentBalance = typeof account.balance?.current === 'number'
  const isClosedWithValue =
    account.status === AccountStatus.CLOSED && hasCurrentBalance

  if (
    account.lob === AccountLOB.CC &&
    isRestrictedCCStatus(account.status) &&
    !isClosedWithValue
  ) {
    return (
      <StyledWrapper>
        <StyledRestrictedAccount
          tag="span"
          size="sm"
          weight="bold"
          {...sharedProps}
          aria-label=""
          aria-hidden
        />
        <ScreenReaderOnly>
          {`${getAccountName(
            nickName,
            name,
          )} account ending in ${maskAccountNumber(accountNumber, 'none')}`}
        </ScreenReaderOnly>
      </StyledWrapper>
    )
  }

  const isCCAccount = account.lob === AccountLOB.CC

  return (
    <StyledWrapper>
      <StyledLink
        {...sharedProps}
        data-testid="account-link"
        to={accountDetailsUrl(type)}
        {...(isCCAccount && {
          onClick: (): void => {
            trackCCAccount(account)
          },
        })}
      />
    </StyledWrapper>
  )
}
